import SvgShippingTruck from "hec/assets/icons/shipping-truck";

import { EligibleProducts } from "~/hec/assets/icons";
import { useRootLayoutData } from "~/root";

// SFCC-11624: the eligibility + free-shipping badges that render below the ATC
// button (inline section, OptimisticBadges seed, and the mobile sticky bar).
// Single source so the icon + font can't drift across the three call sites.
//
// Matches the title-row eligibility chip's visual language (product-details.tsx
// ~L604): brand sprite icons + `font-grotesque`, light weight, 14px, navy
// `#142745` (SFRA blue4 — also the baked fill of the ShippingCheckMark sprite,
// so text + icon read as one tone). The previous generic outline SVGs +
// `text-base font-semibold uppercase` were a Workers invention.

// COM-723: `text-product-badge` is the per-site token (app.css)
const BADGE_CLASS =
  "flex items-center gap-1 sm:gap-2 font-grotesque font-semibold text-xs sm:text-lg text-product-badge";

export function EligibilityBadge({ siteLabel }: { siteLabel: string }) {
  return (
    <div className={BADGE_CLASS}>
      <EligibleProducts
        aria-hidden="true"
        className="h-5 w-4 shrink-0 sm:w-5"
      />
      100% {siteLabel} ELIGIBLE
    </div>
  );
}

export function FreeShippingBadge() {
  // Threshold from BM site prefs via the root loader (EnableProductFreeShipping
  // + FreeShippingMinPrice) — same source SFRA's getFreeShippingMsg reads.
  // null = badge disabled for this site.
  const threshold = useRootLayoutData().freeShipping.badgeThreshold;
  if (threshold == null) return null;
  return (
    <div className={BADGE_CLASS}>
      <SvgShippingTruck
        aria-hidden="true"
        className="w-4.5 shrink-0 sm:w-auto"
      />
      FREE SHIPPING ${threshold}+
    </div>
  );
}

/**
 * SKU-area eligibility summary chip — mirrors SFRA `eligibility.isml` summary
 * mode: bordered pill above the rating. Callers own the SFRA gate
 * (`c_isEligible` + a non-null per-site label); this renders unconditionally so
 * the markup can't drift between the standard and telehealth (ET-216) info
 * columns.
 *
 * Colors: per-site --eligibility-pill-border + --eligibility-pill-text
 * (app.css, prod parity); the check icon (fill=currentColor) inherits
 * --eligibility-pill-text from the wrapper.
 */
export function EligibilitySummaryPill({
  label,
  "data-testid": dataTestId,
}: {
  label: string;
  /**
   * Per-placement id. On a telehealth PDP the chip travels with the title, so it
   * renders once for the mobile (hoisted) title and once for the in-column one —
   * same duplication `pdp-product-name-{mobile,desktop}` already handles.
   */
  "data-testid"?: string;
}) {
  return (
    <div
      data-testid={dataTestId}
      className="inline-flex w-fit items-center gap-1.5 rounded-[15px] border-2 border-eligibility-pill-border px-1 py-px font-grotesque text-sm font-light text-eligibility-pill-text"
    >
      <EligibleProducts aria-hidden="true" className="h-4 w-4 shrink-0" />
      {label}
    </div>
  );
}
